import DynamicsBCIcon from 'ext-ui/svg/icon/DynamicsBCIcon'
import SageIntacctIcon from 'ext-ui/svg/icon/SageIntacctIcon'
import XeroIcon from 'ext-ui/svg/icon/XeroIcon'
import React from 'react'
import QuickBooks from 'src/assets/images/QuickBooks.svg'
import NetSuite from 'src/assets/svgs/NetSuiteIcon.svg'
import {
  OrganizationConnectedPlatform,
  OrganizationIntegrationStatus,
} from 'src/enums'
import {
  BankAccountListModel,
  BankAccountListResponse,
} from 'src/models/bankAccount.models'

export interface OrganizationIntegrationListResponse {
  id: string
  organizationId: string
  connectionIntentId: string
  connectionToken: string
  companyToken: string
  connectionStatus: OrganizationIntegrationStatus
  connectedPlatform: OrganizationConnectedPlatform
  bankAccount?: BankAccountListResponse
  bankAccountOptions?: BankAccountListResponse[]
  lastSyncDate?: string
}

export class OrganizationIntegrationListModel {
  public id: string
  public organizationId: string
  public connectionIntentId: string
  public connectionToken: string
  public companyToken: string
  public connectionStatus: OrganizationIntegrationStatus
  public connectedPlatform: OrganizationConnectedPlatform
  public bankAccount?: BankAccountListModel
  public bankAccountOptions?: BankAccountListModel[]
  public lastSyncDate?: string

  public constructor(data: OrganizationIntegrationListResponse) {
    this.id = data.id
    this.connectionIntentId = data.connectionIntentId
    this.organizationId = data.organizationId
    this.connectionToken = data.connectionToken
    this.companyToken = data.companyToken
    this.connectionStatus = data.connectionStatus
    this.connectedPlatform = data.connectedPlatform
    if (data.bankAccount) {
      this.bankAccount = new BankAccountListModel(data.bankAccount)
    }
    if (data.bankAccountOptions) {
      this.bankAccountOptions = data.bankAccountOptions.map(
        (option) => new BankAccountListModel(option),
      )
    }
    this.lastSyncDate = data.lastSyncDate
  }

  public get logo(): string {
    switch (this.connectedPlatform) {
      case OrganizationConnectedPlatform.NETSUITE:
        return NetSuite
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
      case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE:
        return QuickBooks
      default:
        return ''
    }
  }

  public get iconElement(): React.ReactElement | null {
    switch (this.connectedPlatform) {
      case OrganizationConnectedPlatform.XERO:
        return React.createElement(XeroIcon)
      case OrganizationConnectedPlatform.SAGE_INTACCT:
        return React.createElement(SageIntacctIcon)
      case OrganizationConnectedPlatform.DYNAMICS_BC:
        return React.createElement(DynamicsBCIcon)
      default:
        return null
    }
  }

  public get platformName(): string {
    switch (this.connectedPlatform) {
      case OrganizationConnectedPlatform.NETSUITE:
        return 'NetSuite'
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE:
        return 'QuickBooks'
      case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
        return 'QuickBooks Desktop'
      case OrganizationConnectedPlatform.XERO:
        return 'Xero'
      case OrganizationConnectedPlatform.SAGE_INTACCT:
        return 'Sage Intacct'
      case OrganizationConnectedPlatform.DYNAMICS_BC:
        return 'Dynamics 365 Business Central'
      default:
        return ''
    }
  }

  public get connectionMessage(): string {
    switch (this.connectedPlatform) {
      case OrganizationConnectedPlatform.NETSUITE:
        return 'Connect your NetSuite account to ${appName} so you can tag your transactions with NetSuite tracking categories and push to NetSuite when desired.'
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
      case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
      case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE:
        return 'Connect your QuickBooks account to ${appName} so you can tag your transactions with QuickBooks tracking classes and locations, then push to QuickBooks when desired.'
      case OrganizationConnectedPlatform.XERO:
      case OrganizationConnectedPlatform.SAGE_INTACCT:
      case OrganizationConnectedPlatform.DYNAMICS_BC:
        return 'Seamlessly sync transactions, accounts, and expense categories for faster reconciliation in ${integration}'
      default:
        return ''
    }
  }
}

export const getPlatformName = (
  connectedPlatform: OrganizationConnectedPlatform,
): string => {
  switch (connectedPlatform) {
    case OrganizationConnectedPlatform.NETSUITE:
      return 'NetSuite'
    case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE_SANDBOX:
    case OrganizationConnectedPlatform.QUICKBOOKS_ONLINE:
      return 'QuickBooks Online'
    case OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP:
      return 'QuickBooks Desktop'
    case OrganizationConnectedPlatform.XERO:
      return 'Xero'
    case OrganizationConnectedPlatform.SAGE_INTACCT:
      return 'Sage Intacct'
    case OrganizationConnectedPlatform.DYNAMICS_BC:
      return 'Dynamics 365 Business Central'
    case OrganizationConnectedPlatform.AMEX:
      return 'American Express'
    default:
      return ''
  }
}
